import styled, { useTheme } from 'styled-components';
import Modal from '../../../components/Modal';
import {
  useSetWalletRecovery,
  useMfaEnrollment,
  usePrivy,
} from '@privy-io/react-auth';
import Button from '../../../components/Button';
import { useEffect, useState, useCallback, useRef } from 'react';
import Switch from '../../../components/Switch';
import {
  BodyLarge,
  ParagraphLargeText,
  ParagraphSmallText,
} from '../../../components/Texts';
import { useModalStatus } from '@privy-io/react-auth';
import { useExportWallet } from '@privy-io/react-auth/solana';
import { useDeleteAccountMutation } from '../../../redux/api/authorized/auth';
import { useLogout } from '../../../hooks/useLogout';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const OptionsContainer = styled.div`
  width: 100%;
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DeleteAccountContainer = styled.div`
  margin-top: 24px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.status.error.primary};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.background.primary};
`;

const DeleteAccountText = styled.div`
  margin-bottom: 16px;
`;

const DeleteAccountInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 4px;
  margin-bottom: 16px;
  font-family: inherit;
  resize: vertical;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    width: 100%;
  }
`;

const Settings = ({ onClose }) => {
  const { setWalletRecovery, isWalletRecoveryModalOpen } = useSetWalletRecovery(
    {
      onSuccess: () => {
        setWalletRecoveryEnabled(true);
      },
    },
  );
  const { showMfaEnrollmentModal, isMfaEnrollmentModalOpen } =
    useMfaEnrollment();
  const [walletRecoveryEnabled, setWalletRecoveryEnabled] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [advancedModeEnabled, setAdvancedModeEnabled] = useState(() => {
    return localStorage.getItem('advancedMode') === 'true';
  });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const [deleteAccount, { isLoading: isDeleting }] = useDeleteAccountMutation();
  const { handleLogout } = useLogout(onClose);
  const { user: privyUser } = usePrivy();
  const { exportWallet } = useExportWallet();
  const { isOpen } = useModalStatus();
  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (!isOpenRef.current) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (privyUser?.wallet) {
      setWalletRecoveryEnabled(!!privyUser.wallet.recoveryMethod);
    }
  }, [privyUser?.wallet]);

  useEffect(() => {
    if (privyUser?.mfaMethods) {
      setMfaEnabled(privyUser.mfaMethods.length > 0);
    }
  }, [privyUser?.mfaMethods]);

  const handleWalletRecoveryToggle = async () => {
    await setWalletRecovery();
  };

  const handleMfaToggle = async () => {
    await showMfaEnrollmentModal();
  };

  const handleAdvancedModeToggle = () => {
    const newValue = !advancedModeEnabled;
    setAdvancedModeEnabled(newValue);
    localStorage.setItem('advancedMode', newValue);
  };

  const handleExportWallets = async () => {
    const wallets = await exportWallet();
    console.log(wallets);
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount(deleteReason).unwrap();
      handleLogout();
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  const resetDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
    setDeleteReason('');
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  return (
    <Modal onClose={handleClose}>
      <ParagraphLargeText style={{ color: theme.text.title }}>
        Settings
      </ParagraphLargeText>
      <OptionsContainer>
        <OptionContainer>
          <BodyLarge>Advanced Mode</BodyLarge>
          <Switch
            isOn={advancedModeEnabled}
            onToggle={handleAdvancedModeToggle}
          />
        </OptionContainer>
        <OptionContainer>
          <BodyLarge>Enable Wallet Recovery</BodyLarge>
          <Switch
            isOn={walletRecoveryEnabled}
            onToggle={handleWalletRecoveryToggle}
            disabled={walletRecoveryEnabled}
          />
        </OptionContainer>
        <OptionContainer
          onClick={() => {
            showMfaEnrollmentModal();
          }}
        >
          <BodyLarge>Enable 2FA</BodyLarge>
          <Switch isOn={mfaEnabled} onToggle={handleMfaToggle} />
        </OptionContainer>
      </OptionsContainer>
      <Button
        fullWidth={isMobile}
        onClick={handleExportWallets}
        variant='secondary'
      >
        Export Wallet
      </Button>
      {!showDeleteConfirmation ? (
        <Button
          fullWidth={isMobile}
          onClick={() => setShowDeleteConfirmation(true)}
          variant='danger'
          style={{ marginTop: '12px' }}
        >
          Delete Account
        </Button>
      ) : (
        <DeleteAccountContainer>
          <DeleteAccountText>
            <BodyLarge
              style={{
                color: `${theme.status.error.primary}`,
                marginBottom: '8px',
              }}
            >
              Are you sure you want to delete your account?
            </BodyLarge>
            <ParagraphSmallText style={{ color: `${theme.text.light}` }}>
              This action cannot be undone. All your account data will be
              permanently removed and you will permanently lose access to your
              funds.
            </ParagraphSmallText>
          </DeleteAccountText>

          <DeleteAccountInput
            placeholder='Please provide a reason for deletion (optional)'
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />

          <ButtonContainer>
            <Button
              fullWidth={isMobile}
              onClick={resetDeleteConfirmation}
              variant='secondary'
            >
              Cancel
            </Button>
            <Button
              fullWidth={isMobile}
              onClick={handleDeleteAccount}
              variant='danger'
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Yes, Delete My Account'}
            </Button>
          </ButtonContainer>
        </DeleteAccountContainer>
      )}
    </Modal>
  );
};

export default Settings;
